import React, { useState } from 'react';
import { Bill } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { Receipt, CheckCircle2, XCircle, Plus, X, Calendar, Edit2 } from 'lucide-react';
interface BillsPageProps {
  bills: Bill[];
  onAddBill: (bill: Omit<Bill, 'id'>) => void;
  onUpdateBill: (bill: Bill) => void;
  onTogglePaid: (id: string) => void;
  onDeleteBill: (id: string) => void;
}
export function BillsPage({
  bills,
  onAddBill,
  onUpdateBill,
  onTogglePaid,
  onDeleteBill
}: BillsPageProps) {
  const [showModal, setShowModal] = useState(false);
  const [editingBill, setEditingBill] = useState<Bill | null>(null);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Bills');
  const [dueDate, setDueDate] = useState('1');
  const [isRecurring, setIsRecurring] = useState(true);
  const openModal = (bill?: Bill) => {
    if (bill) {
      setEditingBill(bill);
      setName(bill.name);
      setAmount(bill.amount.toString());
      setCategory(bill.category);
      setDueDate(bill.dueDate.toString());
      setIsRecurring(bill.isRecurring);
    } else {
      setEditingBill(null);
      setName('');
      setAmount('');
      setCategory('Bills');
      setDueDate('1');
      setIsRecurring(true);
    }
    setShowModal(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingBill) {
      onUpdateBill({
        ...editingBill,
        name,
        amount: parseFloat(amount),
        category,
        dueDate: parseInt(dueDate),
        isRecurring
      });
    } else {
      onAddBill({
        name,
        amount: parseFloat(amount),
        category,
        dueDate: parseInt(dueDate),
        isRecurring,
        isPaid: false
      });
    }
    setShowModal(false);
  };
  const upcomingBills = bills.filter(b => !b.isPaid);
  const paidBills = bills.filter(b => b.isPaid);
  return <div className="space-y-6 pb-24" data-id="element-269">
      <div className="flex justify-between items-center px-2" data-id="element-270">
        <h1 className="text-2xl font-bold text-stone-50" data-id="element-271">Bills</h1>
        <button onClick={() => openModal()} className="p-2 bg-amber-500 text-stone-900 rounded-full hover:bg-amber-400 transition-colors" data-id="element-272">
          <Plus size={20} data-id="element-273" />
        </button>
      </div>

      {/* Upcoming Bills */}
      <div data-id="element-274">
        <h3 className="text-stone-400 text-sm font-bold uppercase tracking-wider mb-3 px-2" data-id="element-275">
          Upcoming ({upcomingBills.length})
        </h3>
        <div className="space-y-3" data-id="element-276">
          {upcomingBills.length === 0 ? <div className="text-center py-12 text-stone-500" data-id="element-277">
              <Receipt size={48} className="mx-auto mb-4 opacity-50" data-id="element-278" />
              <p data-id="element-279">No upcoming bills</p>
            </div> : upcomingBills.map((bill, i) => <motion.div key={bill.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.05
        }} className="bg-stone-800 rounded-2xl p-5 border border-stone-700/30" data-id="element-280">
                <div className="flex justify-between items-start mb-4" data-id="element-281">
                  <div className="flex items-center gap-3" data-id="element-282">
                    <div className="p-3 rounded-xl bg-red-500/10 text-red-500" data-id="element-283">
                      <Receipt size={20} data-id="element-284" />
                    </div>
                    <div data-id="element-285">
                      <h3 className="text-stone-100 font-bold" data-id="element-286">{bill.name}</h3>
                      <p className="text-stone-500 text-xs" data-id="element-287">
                        Due: Day {bill.dueDate} • {bill.category}
                      </p>
                      {bill.isRecurring && <span className="inline-block px-2 py-0.5 bg-stone-700 rounded-md text-[10px] text-stone-300 mt-1" data-id="element-288">
                          Recurring
                        </span>}
                    </div>
                  </div>
                  <span className="text-xl font-bold text-stone-50" data-id="element-289">
                    ${bill.amount.toLocaleString()}
                  </span>
                </div>

                <div className="flex gap-2" data-id="element-290">
                  <button onClick={() => onTogglePaid(bill.id)} className="flex-1 py-3 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors" data-id="element-291">
                    <CheckCircle2 size={16} data-id="element-292" />
                    Mark as Paid
                  </button>
                  <button onClick={() => openModal(bill)} className="px-4 py-3 bg-stone-900 hover:bg-stone-950 text-amber-400 rounded-xl transition-colors" data-id="element-293">
                    <Edit2 size={16} data-id="element-294" />
                  </button>
                  <button onClick={() => {
              if (confirm('Delete this bill?')) onDeleteBill(bill.id);
            }} className="px-4 py-3 bg-stone-900 hover:bg-stone-950 text-red-400 rounded-xl transition-colors" data-id="element-295">
                    <X size={16} data-id="element-296" />
                  </button>
                </div>
              </motion.div>)}
        </div>
      </div>

      {/* Paid Bills */}
      {paidBills.length > 0 && <div data-id="element-297">
          <h3 className="text-stone-400 text-sm font-bold uppercase tracking-wider mb-3 px-2" data-id="element-298">
            Paid ({paidBills.length})
          </h3>
          <div className="space-y-3" data-id="element-299">
            {paidBills.map((bill, i) => <motion.div key={bill.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.05
        }} className="bg-stone-800/50 rounded-2xl p-5 border border-stone-700/20 opacity-60" data-id="element-300">
                <div className="flex justify-between items-center mb-3" data-id="element-301">
                  <div className="flex items-center gap-3" data-id="element-302">
                    <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-500" data-id="element-303">
                      <CheckCircle2 size={20} data-id="element-304" />
                    </div>
                    <div data-id="element-305">
                      <h3 className="text-stone-100 font-bold" data-id="element-306">{bill.name}</h3>
                      <p className="text-stone-500 text-xs" data-id="element-307">
                        Paid{' '}
                        {bill.lastPaidDate ? new Date(bill.lastPaidDate).toLocaleDateString() : 'Recently'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-308">
                    <span className="text-lg font-bold text-stone-50" data-id="element-309">
                      ${bill.amount.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-310">
                  <button onClick={() => onTogglePaid(bill.id)} className="flex-1 py-2 text-xs text-stone-500 hover:text-amber-500" data-id="element-311">
                    Undo Payment
                  </button>
                  <button onClick={() => openModal(bill)} className="px-3 py-2 text-xs text-stone-500 hover:text-amber-500" data-id="element-312">
                    <Edit2 size={14} data-id="element-313" />
                  </button>
                </div>
              </motion.div>)}
          </div>
        </div>}

      {/* Add/Edit Bill Modal */}
      <AnimatePresence data-id="element-314">
        {showModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowModal(false)} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-315" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="fixed bottom-0 left-0 right-0 bg-stone-900 rounded-t-3xl z-50 p-6 max-h-[80vh] overflow-y-auto border-t border-stone-800" data-id="element-316">
              <div className="flex justify-between items-center mb-6" data-id="element-317">
                <h2 className="text-2xl font-bold text-stone-50" data-id="element-318">
                  {editingBill ? 'Edit Bill' : 'Add Bill'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 bg-stone-800 rounded-full text-stone-400 hover:text-stone-200" data-id="element-319">
                  <X size={20} data-id="element-320" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-321">
                <div data-id="element-322">
                  <label className="block text-xs font-medium text-stone-500 uppercase mb-1" data-id="element-323">
                    Bill Name
                  </label>
                  <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-stone-800 text-stone-50 py-4 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" placeholder="e.g., Electricity, Rent" data-id="element-324" />
                </div>

                <div data-id="element-325">
                  <label className="block text-xs font-medium text-stone-500 uppercase mb-1" data-id="element-326">
                    Amount
                  </label>
                  <div className="relative" data-id="element-327">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 text-lg" data-id="element-328">
                      $
                    </span>
                    <input type="number" step="0.01" required value={amount} onChange={e => setAmount(e.target.value)} className="w-full bg-stone-800 text-stone-50 text-2xl font-bold py-4 pl-10 pr-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" placeholder="0.00" data-id="element-329" />
                  </div>
                </div>

                <div data-id="element-330">
                  <label className="block text-xs font-medium text-stone-500 uppercase mb-1" data-id="element-331">
                    Due Date (Day of Month)
                  </label>
                  <input type="number" min="1" max="31" required value={dueDate} onChange={e => setDueDate(e.target.value)} className="w-full bg-stone-800 text-stone-50 py-4 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500/50" data-id="element-332" />
                </div>

                <div className="bg-stone-800/50 p-4 rounded-2xl border border-stone-800" data-id="element-333">
                  <label className="flex items-center gap-3 cursor-pointer" data-id="element-334">
                    <input type="checkbox" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} className="w-5 h-5 rounded-md border-stone-600 text-amber-500 focus:ring-amber-500 bg-stone-700" data-id="element-335" />
                    <span className="text-stone-300" data-id="element-336">Recurring monthly</span>
                  </label>
                </div>

                <button type="submit" className="w-full py-4 bg-amber-500 text-stone-900 font-bold rounded-2xl hover:bg-amber-400 transition-colors flex items-center justify-center gap-2" data-id="element-337">
                  <CheckCircle2 size={20} data-id="element-338" />
                  {editingBill ? 'Update Bill' : 'Add Bill'}
                </button>
              </form>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}